/**
 * Pure client-side case filters for the Workforce Console.
 *
 * The console fetches cases per team (across all of a team's programs) and
 * narrows by program in-memory, so the program filter lives here rather than in
 * the fetch fan-out.
 */
import type { Program } from '@compass/client/types/program';
import type { Team } from '@compass/client/types/team';

/**
 * Narrow a list of cases to those belonging to one of `selectedProgramIds`.
 * An empty selection means "all programs" and returns the input array unchanged
 * (same reference) so callers keep referential stability. Cases without a
 * `programId` are dropped only when a filter is active.
 */
export function filterCasesByPrograms<T extends { programId?: string }>(
  cases: T[],
  selectedProgramIds: string[],
): T[] {
  if (selectedProgramIds.length === 0) return cases;
  const selected = new Set(selectedProgramIds);
  return cases.filter(c => c.programId !== undefined && selected.has(c.programId));
}

/** Minimal raw-case shape the workflow-scope filter reads. */
export interface WorkflowScopeCase {
  assigned_user_id?: string | undefined;
  workflow_type?: string | undefined;
  program_id?: string | undefined;
}

/**
 * Narrow assignee-fetched cases to those inside the selected teams'
 * (team → program → workflow-type) scope.
 *
 * The console fetches cases by ASSIGNEE — the union of the selected teams'
 * members, per program — and drops the team dimension entirely (BI-1468). That
 * over-fetches whenever a member also belongs to another team: selecting a
 * medbv-only team would also show that member's PBM case, because nothing
 * re-checks the team's `workflow_types` after the union. This restores that
 * precision client-side, without re-introducing the per-(team, program) fetch
 * fan-out BI-1468 removed.
 *
 * A case is kept iff SOME selected team T satisfies all of:
 *   - T serves the case's program (`T.id ∈ program.teams[]`);
 *   - the case's assignee is on T (`member_ids ∪ {manager_id}`);
 *   - T serves the case's workflow type (`workflow_type ∈ T.workflow_types`).
 *
 * Leniency — never hide a case we can't positively rule out of scope:
 *   - empty `scopeTeams` → input returned unchanged (no scope to apply);
 *   - a case with no `assigned_user_id` → kept (can't attribute it to a team);
 *   - a team with empty/unset `workflow_types` → serves ALL workflow types (this
 *     mirrors the reassign target-team fallback and avoids hiding everything for
 *     teams created before the field existed);
 *   - a case with no `workflow_type` → the workflow-type check is skipped;
 *   - a program not resolvable in `programs` → its serve check is skipped.
 */
export function filterCasesByTeamWorkflowScope<T extends WorkflowScopeCase>(
  cases: T[],
  scopeTeams: Team[],
  programs: Program[],
): T[] {
  if (scopeTeams.length === 0) return cases;

  const teamsByProgram = new Map<string, Set<string>>();
  for (const p of programs) {
    if (p.id) teamsByProgram.set(p.id, new Set(p.teams ?? []));
  }

  return cases.filter(c => {
    const assignee = c.assigned_user_id;
    if (!assignee) return true;
    const servingTeams = c.program_id ? teamsByProgram.get(c.program_id) : undefined;
    const workflowType = c.workflow_type;
    return scopeTeams.some(t => {
      if (!t.id) return false;
      // Team must serve this program — skipped when the program can't be resolved.
      if (servingTeams && !servingTeams.has(t.id)) return false;
      const isMember = t.manager_id === assignee || (t.member_ids ?? []).includes(assignee);
      if (!isMember) return false;
      const wfTypes = t.workflow_types ?? [];
      return wfTypes.length === 0 || !workflowType || wfTypes.includes(workflowType);
    });
  });
}
